import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, PaperPlaneTilt } from "@phosphor-icons/react";
import api from "@/lib/api";
import { CASE_TYPES, StateOptions, TestLeadResult } from "@/components/SendTestLeadButton";
import { funnelTitle, picksOrAll } from "@/lib/funnelOptions";
import { TestLeadsTable } from "@/components/testleads/TestLeadsTable";

const INPUT = "mt-1 w-full border-2 border-black bg-white px-2 py-1.5 text-sm";
const BTN = "border-2 border-black bg-black px-4 py-2 text-xs uppercase tracking-widest text-white hover:bg-zinc-800 inline-flex items-center gap-1.5 disabled:opacity-40";
const EMPTY = { buyer: "", page: "", state: "", case_type: "", first_name: "", last_name: "", email: "", phone: "" };

function Field({ label, children }) {
  return <label className="block text-xs">{label}{children}</label>;
}

function pageOptions(buyer, landers) {
  const funnels = picksOrAll(buyer?.picks).map((f) => ({ v: `funnel:${f}`, l: `Shared page: ${funnelTitle(f)}` }));
  return [...funnels, ...landers.map((l) => ({ v: `page:${l.slug}`, l: `Their page: /lp/${l.slug}${l.status !== "published" ? " (draft)" : ""}` }))];
}

function TestLeadForm({ buyers, onSent }) {
  const [f, setF] = useState(EMPTY);
  const [landers, setLanders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const buyer = buyers.find((b) => b.id === f.buyer);
  useEffect(() => {
    setLanders([]);
    if (!f.buyer) return;
    api.get(`/admin/buyers/${f.buyer}/landers`).then((r) => setLanders(r.data.landers)).catch(() => {});
  }, [f.buyer]);
  const options = useMemo(() => pageOptions(buyer, landers), [buyer, landers]);
  const send = async () => {
    const page = f.page || options[0]?.v || "funnel:standard";
    const body = { buyer_page_id: f.buyer, ...(page.startsWith("page:") ? { page_slug: page.slice(5) } : { funnel: page.slice(7) }) };
    ["state", "case_type", "first_name", "last_name", "email", "phone"].forEach((k) => { if (f[k].trim()) body[k] = f[k].trim(); });
    setBusy(true);
    try {
      const { data } = await api.post("/admin/global-funnels/test-lead", body);
      setResult(data);
      toast.success(`Test lead sent to ${buyer.firm_name}`);
      onSent();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't send the test lead");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="border-2 border-black bg-white p-4" data-testid="test-leads-form">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Buyer">
          <select className={INPUT} value={f.buyer} onChange={(e) => setF({ ...EMPTY, buyer: e.target.value })} data-testid="test-leads-buyer">
            <option value="">Pick a buyer</option>
            {buyers.map((b) => (
              <option key={b.id} value={b.id} disabled={!b.delivery}>{b.firm_name}{b.delivery ? "" : " (add a delivery first)"}{b.status !== "published" ? " · not published" : ""}</option>
            ))}
          </select>
        </Field>
        <Field label="Comes from page">
          <select className={INPUT} value={f.page} onChange={set("page")} disabled={!buyer} data-testid="test-leads-page">
            {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
        <Field label="Visitor's state">
          <select className={INPUT} value={f.state} onChange={set("state")} data-testid="test-leads-state">
            <option value="">Pick one the buyer takes</option>
            <StateOptions served={buyer?.states} label="name" />
          </select>
        </Field>
        <Field label="Case type">
          <select className={INPUT} value={f.case_type} onChange={set("case_type")} data-testid="test-leads-case">
            <option value="">Match the page</option>
            {CASE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="First name (optional)"><input className={INPUT} value={f.first_name} maxLength={40} onChange={set("first_name")} placeholder="Test" data-testid="test-leads-first" /></Field>
        <Field label="Last name (optional)"><input className={INPUT} value={f.last_name} maxLength={40} onChange={set("last_name")} placeholder="Lead 1234" data-testid="test-leads-last" /></Field>
        <Field label="Email (optional)"><input className={INPUT} value={f.email} type="email" onChange={set("email")} placeholder="test-lead-…@example.com" data-testid="test-leads-email" /></Field>
        <Field label="Phone (optional)"><input className={INPUT} value={f.phone} type="tel" onChange={set("phone")} placeholder="555…" data-testid="test-leads-phone" /></Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" className={BTN} disabled={!buyer?.delivery || busy} onClick={send} data-testid="test-leads-send">
          <PaperPlaneTilt size={13} weight="bold" /> {busy ? "Sending…" : "Send test lead"}
        </button>
        <span className="text-[11px] text-zinc-600">Marked TEST, goes through the buyer&apos;s real delivery, and stays out of billing and reports.</span>
      </div>
      {result && <div className="mt-3"><TestLeadResult r={result} showLeadLink testId="test-leads" /></div>}
    </section>
  );
}


/** Send a TEST lead to any buyer from any of their pages, then see whether it reached them. */
export default function AdminTestLeads() {
  const [buyers, setBuyers] = useState(null);
  const [recent, setRecent] = useState({ leads: [], total: 0, matched: 0 });
  const [q, setQ] = useState("");
  const seq = useRef(0);
  const loadRecent = useCallback(() => {
    const n = ++seq.current;
    api.get("/admin/test-leads", { params: q.trim() ? { q: q.trim() } : {} })
      .then((r) => n === seq.current && setRecent({ leads: r.data.leads, total: r.data.total ?? r.data.leads.length, matched: r.data.matched ?? r.data.leads.length }))
      .catch(() => {});
  }, [q]);
  useEffect(() => {
    api.get("/admin/global-funnels").then((r) => setBuyers((r.data.buyers || []).filter((b) => !b.is_sample_page)))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load buyers"));
  }, []);
  useEffect(() => {
    const t = setTimeout(loadRecent, 300);
    return () => { clearTimeout(t); };
  }, [loadRecent]);
  return (
    <div className="space-y-6" data-testid="test-leads-page">
      <div>
        <div className="label-mono">Delivery check</div>
        <h1 className="font-chivo text-4xl font-black uppercase tracking-tighter sm:text-5xl">Test leads</h1>
        <p className="mt-2 max-w-3xl text-sm text-zinc-700">
          Pick a buyer and the page the lead should come from, then send. It works as soon as the buyer has a delivery,
          even before their pages are published.
        </p>
      </div>
      {!buyers ? <div className="label-mono">Loading…</div> : <TestLeadForm buyers={buyers} onSent={loadRecent} />}
      <div>
        <div className="mb-2 flex flex-wrap items-end gap-3">
          <div className="label-mono">Recent test leads</div>
          <label className="relative ml-auto w-full sm:w-80">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input className="w-full border-2 border-black bg-white py-1.5 pl-7 pr-2 text-sm" value={q} maxLength={100}
                   onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, buyer or lead id"
                   aria-label="Search test leads" data-testid="test-leads-search" />
          </label>
        </div>
        {q.trim() && <div className="label-mono mb-2 text-zinc-600" data-testid="test-leads-search-count">{recent.matched} match{recent.matched === 1 ? "" : "es"}</div>}
        <TestLeadsTable rows={recent.leads} total={recent.total} searching={Boolean(q.trim())} onChanged={loadRecent} />
      </div>
    </div>
  );
}
